import { ref } from 'vue';
import { Conflict, Exists, InvalidEmail, InvalidName, InvalidGroup } from './types';
import { email, minLength, required } from '../../rules';
import { UserToCreate } from '../..';
import { useLocale } from '../../composables/useLocale';
import { CustomerMgmtTypes, useGetOrganizationUsers } from '@ilteducation/customer-mgmt';
import type { SlimOrganization } from '@ilteducation/my-account-types';
import { isGroupLevelRestriction } from '@ilteducation/customer-mgmt-permissions';
import pMap from 'p-map';
import { CompleteEvent } from './index.vue';
import { Role } from '../../types/user';

const isUserToUpdate = (user: UserToCreate): boolean => !!user.uid;
const isEmptyUser = (user: UserToCreate): boolean => user.name === '' && user.email === '';

export const fullnameRules = (user: UserToCreate, t: (key: string, ...params: unknown[]) => string) =>
  isUserToUpdate(user) ? [] : [minLength(4, t)];
export const emailRule = (user: Pick<UserToCreate, 'role'>, t: (key: string, ...params: unknown[]) => string) =>
  user.role === 'student' ? [email(t)] : [required(t), email(t)];
export const groupRule = (isGroupLevel: boolean, t: (key: string, ...params: unknown[]) => string) =>
  isGroupLevel ? [required(t)] : [];

export const useConflictManager = (organization: SlimOrganization) => {
  const organizationUid = organization.uid;
  const users = ref<UserToCreate[]>([]);
  const conflicts = ref<Conflict[]>([]);
  const { loadUsers: searchUsers } = useGetOrganizationUsers();

  const { t } = useLocale();

  const validateName = (user: UserToCreate) => {
    const validation = user.validations ?? {};
    delete validation['invalid-name'];
    const rules = fullnameRules(user, t);

    rules.some((rule) => {
      const result = rule(user.name);
      if (result !== true) {
        const conflict: InvalidName = {
          type: 'invalid-name',
          keys: user.key,
          user,
          message: t('lcp.invalid_input'),
        };

        if (typeof result === 'string') {
          conflict.message = t(result);
        }
        validation[conflict.type] = conflict;
        return true;
      }
      return false;
    });
    user.validations = validation;
  };

  const validateEmail = (user: UserToCreate) => {
    const rules = emailRule(user, t);
    const validations = user.validations ?? {};

    delete validations['invalid-email'];
    rules.some((rule) => {
      const result = rule(user.email);
      if (result !== true) {
        const conflict: InvalidEmail = {
          type: 'invalid-email',
          keys: user.key,
          user,
          message: t('lcp.invalid_input'),
        };
        if (typeof result === 'string') {
          conflict.message = t(result);
        }
        validations['invalid-email'] = conflict;
        return true;
      }
      return false;
    });
    user.validations = validations;
  };

  const validateGroup = (user: UserToCreate) => {
    const rules = groupRule(isGroupLevelRestriction(organization), t);
    const validations = user.validations ?? {};

    delete validations['invalid-group'];
    rules.some((rule) => {
      const result = rule(user.groupUid);
      if (result !== true) {
        const conflict: InvalidGroup = {
          type: 'invalid-group',
          keys: user.key,
          user,
          message: t('lcp.invalid_input'),
        };
        if (typeof result === 'string') {
          conflict.message = t(result);
        }
        validations['invalid-group'] = conflict;
        return true;
      }
      return false;
    });
    user.validations = validations;
  };

  const makeUsersByName = (users: UserToCreate[]) => {
    const usersByNameMap: Record<string, UserToCreate[]> = {};
    users.forEach((user) => {
      const nameKey = user.name.toLowerCase().trim();
      if (!usersByNameMap[nameKey]) {
        usersByNameMap[nameKey] = [];
      }
      usersByNameMap[nameKey].push(user);
    });
    return usersByNameMap;
  };

  const findDuplicates = (users: UserToCreate[]) => {
    const usersByNameMap = makeUsersByName(users);
    users.forEach((user: UserToCreate) => {
      const validations = user.validations ?? {};
      const usersWithDuplicateName = usersByNameMap[user.name.toLowerCase().trim()] ?? [];
      if (usersWithDuplicateName.length > 1) {
        validations['duplicate'] = {
          type: 'duplicate',
          keys: Object.values(usersWithDuplicateName)
            .map((e) => e.key)
            .sort()
            .join('-'),
          users: Object.values(usersWithDuplicateName)!,
          message: t('lcp.duplicate'),
        };
      }

      user.validations = validations;
    });
  };

  const clearDuplicates = (users: UserToCreate[]) => {
    users.forEach((user) => {
      if (user.validations && user.validations['duplicate']) {
        delete user.validations['duplicate'];
        if (Object.keys(user.validations).length === 0) {
          user.validations = undefined;
        }
      }
    });
  };

  const onSuggestionFind = (user: UserToCreate, data: CustomerMgmtTypes.GetOrganizationUsersResponse['data']) => {
    const validations = user.validations ?? {};
    delete validations['exists'];

    if (data.length) {
      const conflict: Exists = {
        keys: user.key,
        type: 'exists',
        user,
        users: data,
        message: t('lcp.already_exists'),
      };

      validations['exists'] = conflict;
    }
    user.validations = validations;
  };

  const allUsersAreValid = ref(false);
  const findExistingUsers = async (
    schoolUid: string,
    parsedUsers: UserToCreate[],
    callback: (user: UserToCreate, data: CustomerMgmtTypes.GetOrganizationUsersResponse['data']) => void,
  ) => {
    const usersToValidate = parsedUsers.filter(
      // if user.uid exists, the user data will be updated instead of creation.
      (user) =>
        (user.existCheckHash === undefined || user.existCheckHash !== `${user.name}-${user.email}`) &&
        !isUserToUpdate(user),
    );
    if (usersToValidate.length === 0) {
      return;
    }

    await pMap(usersToValidate, async (user) => await findExistingForUser(user, schoolUid, callback), {
      concurrency: 5,
    });
  };

  const findExistingForUser = async (
    user: UserToCreate,
    schoolUid: string,
    callback: (user: UserToCreate, data: CustomerMgmtTypes.GetOrganizationUsersResponse['data']) => void,
  ) => {
    const originalUserStatus = user.status;
    user.status = 'checkingExistingUsers';

    const possibleUsers = await searchUsers(schoolUid, {
      pageNumber: 1,
      pageSize: 10,
      sortBy: 'firstname',
      sortOrder: 'asc',
      q: {
        name: user.name,
        email: user.email || undefined,
      },
    }).catch((error) => {
      console.error('Failed checking existing users', error);
      return { data: [], meta: { totalItems: 0 } };
    });

    allUsersAreValid.value = allUsersAreValid.value && true;
    user.existCheckHash = `${user.name}-${user.email}`;
    user.status = originalUserStatus;

    callback(user, possibleUsers.data);
  };

  const publishConflict = () => {
    const userConflicts: Conflict[] = users.value.flatMap((u) => Object.values(u.validations ?? {}));
    const conflictsByTypeAndKeys = new Map(userConflicts.map((c) => [c.type + c.keys, c]));
    conflicts.value = [...conflictsByTypeAndKeys.values()];
  };

  const isValidating = ref<boolean>(false);
  const validateAll = async () => {
    isValidating.value = true;
    const usersToValidate = users.value.filter((user) => !isEmptyUser(user));
    usersToValidate.forEach((user) => {
      validateName(user);
      validateEmail(user);
      validateGroup(user);
    });

    clearDuplicates(usersToValidate);
    findDuplicates(usersToValidate);

    await findExistingUsers(organizationUid, usersToValidate, onSuggestionFind);

    publishConflict();
    isValidating.value = false;
  };

  const addUsersToValidation = (newUsers?: UserToCreate[]) => {
    if (Array.isArray(newUsers)) {
      users.value.push(...newUsers);
      validateAll();
    }
  };

  const resolve = (event: CompleteEvent) => {
    const removedKeys = event.removeUsers.map((u) => u.key);
    users.value = users.value.filter((u) => !removedKeys.includes(u.key));

    // Update properties
    Object.keys(event.propertyComplete ?? {}).forEach((key: string) => {
      const userIndex = users.value.findIndex((u) => u.key === key);
      if (userIndex > -1) {
        users.value[userIndex] = {
          ...users.value[userIndex],
          ...event.propertyComplete[key],
        };
      }
    });

    // use existing ones
    Object.entries(event.useInstead).forEach(([key, value]) => {
      const user = users.value.find((u) => u.key === key);
      if (user) {
        user.uid = value.uid;
        user.email = value.email ?? undefined;
        user.role = value.association.role as Role;
        user.name = value.fullname;
        user.userName = value.username;
        user.active = value.active;
        if (user.validations) delete user.validations['exists'];
      }
    });

    // resolved
    event.resolvedKeys.forEach((key) => {
      const user = users.value.find((u) => u.key === key);
      if (user) {
        user.validations = undefined;
      }
    });

    publishConflict();
  };

  const resolveSingleConflict = (event: CompleteEvent, conflict: Conflict) => {
    const originalUsers: Record<string, UserToCreate> = {};
    const removedKeys = event.removeUsers.map((u) => u.key);
    users.value = users.value.filter((u) => !removedKeys.includes(u.key));

    // Update properties
    Object.keys(event.propertyComplete ?? {}).forEach((key: string) => {
      const userIndex = users.value.findIndex((u) => u.key === key);
      if (userIndex > -1) {
        users.value[userIndex] = {
          ...users.value[userIndex],
          ...event.propertyComplete[key],
        };
      }
    });

    // use existing ones
    Object.entries(event.useInstead).forEach(([key, value]) => {
      const user = users.value.find((u) => u.key === key);
      if (!user) return;

      originalUsers[user.key] = { ...user }; // Store original user for later use
      if (user) {
        user.uid = value.uid;
        user.email = value.email ?? undefined;
        user.role = value.association.role;
        user.name = value.fullname;
        user.userName = value.username;
        if (user.validations) delete user.validations['exists'];
      }
    });

    // resolved
    event.resolvedKeys.forEach((key) => {
      const resolvedUser = users.value.find((u) => u.key === key);

      if (resolvedUser) {
        if (resolvedUser.validations && resolvedUser.validations[conflict.type]) {
          delete resolvedUser.validations[conflict.type];
        }
        if (Object.keys(resolvedUser.validations ?? {}).length === 0) {
          resolvedUser.validations = undefined;
        }

        // If the conflict type is 'exists' and the user replaced
        // and if the name has changed,
        // Remove the duplicate lcp.

        if (conflict.type === 'exists' && event.useInstead[key]) {
          const usersWithSameNewOrOriginalName = users.value.filter((u) => {
            return (
              u.key !== key &&
              (u.name.toLowerCase().trim() === resolvedUser.name.toLowerCase().trim() ||
                u.name.toLowerCase().trim() === originalUsers[resolvedUser.key].name.toLowerCase().trim())
            );
          });

          clearDuplicates([resolvedUser, ...usersWithSameNewOrOriginalName]);
          findDuplicates([resolvedUser, ...usersWithSameNewOrOriginalName]);
        }
      }
    });

    publishConflict();
  };

  const removeUserByKey = (key: string) => {
    const index = users.value.findIndex((user) => user.key === key);
    if (index > -1) {
      users.value.splice(index, 1);
      validateAll();
    }
  };

  const reset = () => {
    users.value = [];
    validateAll();
  };

  return {
    removeUserByKey,
    validateAll,
    conflicts,
    isValidating,
    resolve,
    resolveSingleConflict,
    reset,
    users,
    addUsersToValidation,
  };
};
